import { useEffect, useState } from "react";
import { ClipLoader } from "react-spinners";
import { toast } from 'react-toastify';
import {competitionsSubmissionsCreate, competitionsModelSubmissionsCreate, competitionsLangflowSubmissionsCreate} from "@/api/generated/backendAPI"
import { AiOutlineCloudUpload } from "react-icons/ai";
import { getUploadErrorMessage } from "@/utils/utils"

type Props = {
  submissionType: "results" | "results_json" | "program" | "pptx";
  licenseId: string;
  competitionId: number;
  evaluationMetricsName?: string;
  closeModal: (uploaded?: boolean) => void;
};

const FILE_INPUT_ACCEPT = {
  results: ".csv",
  results_json: ".json",
  program: ".zip",
  pptx: ".pptx,.json"
}

// 提出物が CSV ではない評価指標。submissionType が results でも採点側は JSON として読む
// （cmp-pjt-back の validators/submission_csv.py の NON_CSV_METRICS と合わせる）
const NON_CSV_METRICS = ["ai_challenge_day"]

const getFileInputAccept = (submissionType: Props["submissionType"], evaluationMetricsName?: string) => {
  if (submissionType === "results" && evaluationMetricsName && NON_CSV_METRICS.includes(evaluationMetricsName)) {
    return ".json"
  }
  return FILE_INPUT_ACCEPT[submissionType]
}

type MultipleFiles = {
  pptx1: File | null;
  pptx2: File | null;
  pptx3: File | null;
  pptx4: File | null;
  pptx5: File | null;
  json: File | null;
};

function FileUpload(props: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [multipleFiles, setMultipleFiles] = useState<MultipleFiles>({
    pptx1: null,
    pptx2: null,
    pptx3: null,
    pptx4: null,
    pptx5: null,
    json: null,
  })
  const [status, setStatus] = useState<string>("available")
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const fileInputAccept = getFileInputAccept(props.submissionType, props.evaluationMetricsName)

  const handleFileSubmit = async() => {
    if(props.submissionType === "pptx"){
      // Validate required files: at least one PPTX file and JSON file are required
      const hasPptxFile = multipleFiles.pptx1 || multipleFiles.pptx2 || multipleFiles.pptx3 || multipleFiles.pptx4;

      if(!hasPptxFile || !multipleFiles.json){
        toast.error("必須ファイルをアップロードしてください（パワーポイントファイル1つ以上、JSONファイル）", {
          position: "bottom-left",
        });
        return;
      }

      setStatus("uploading");

      try {
        // Call the Langflow submission API with File objects directly
        const response = await competitionsLangflowSubmissionsCreate(props.licenseId, {
          flowJson: multipleFiles.json as unknown as string,
          pptxFile1: multipleFiles.pptx1 as unknown as string | null,
          pptxFile2: multipleFiles.pptx2 as unknown as string | null,
          pptxFile3: multipleFiles.pptx3 as unknown as string | null,
          pptxFile4: multipleFiles.pptx4 as unknown as string | null,
          pptxFile5: null, // Level 5 file upload is disabled
          fileName: multipleFiles.json?.name,
        });

        if (response.message === "Langflow submission uploaded successfully") {
          toast.success("アップロードが完了しました！", {
            position: "bottom-left",
          });
          props.closeModal(true);
        } else {
          // Handle submission limit case (204 No Content or other messages)
          const message = response.message || "投稿回数の上限に達しました";
          toast.info(message, {
            position: "bottom-left",
          });
          props.closeModal();
        }
      } catch (e) {
        console.error("Upload error:", e);
        toast.error("アップロードに失敗しました。", {
          position: "bottom-left",
        });
      } finally {
        setStatus("available");
      }
      return;
    }

    if(!file){
      return
    }
    if(props.submissionType === "results" || props.submissionType === "results_json"){
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = async() => {
        if (reader.result && typeof reader.result === "string"){
          setStatus("uploading")
          try{
            await competitionsSubmissionsCreate(props.licenseId, {
            file: reader.result,
            fileName: file.name,
            })
            toast.success("アップロードが完了しました！", {
              position: "bottom-left",
            });
            props.closeModal();
          }catch (e){
            toast.error(getUploadErrorMessage(e), {
              position: "bottom-left",
            })
          }finally{
            setStatus("available");
          }
        }
      }
    }
    if(props.submissionType === "program"){
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = async() => {
        if (reader.result && typeof reader.result === "string"){
          setStatus("uploading")
          try{
            const response = await competitionsModelSubmissionsCreate(props.licenseId,
              {
                file: reader.result
              }
            )
            const message:string = String(response.message);
            if (message == "ok") {
              toast.success("アップロードが完了しました！", {
                position: "bottom-left",
              });
              props.closeModal();
            }
            else {
              toast.error(message, {
                position: "bottom-left",
              });
            }
          }catch (e){
            toast.error("アップロードに失敗しました。", {
              position: "bottom-left",
            })
          }finally{
            setStatus("available");
          }
        }
      }
    }
  }



  const displayFileSize = (size: number) : string => {
    let roundedSize = size
    if(size > 100000){
      roundedSize = Math.round(size / 100000) / 10
      return roundedSize.toString() + "MB"
    }else if(size > 100){
      roundedSize = Math.round(size / 100) / 10
      return roundedSize.toString() + "KB"
    }
    return (Math.round(size * 10) / 10).toString() + "B"
  }
  useEffect(() => {
    if (props.competitionId === 0 || props.licenseId === "") {
      toast.error("リクエストの情報が正しくありません。", {
        position: "bottom-left",
      });
      setStatus("available")
      setFile(null)
      props.closeModal()
      // TODO エラー表示
    }
  }, [props])
  useEffect(() => {
    if(props.submissionType === "pptx"){
      // Check if required files are uploaded for pptx type
      // At least one PPTX file and JSON file are required
      const hasPptxFile = multipleFiles.pptx1 || multipleFiles.pptx2 || multipleFiles.pptx3 || multipleFiles.pptx4;
      if(hasPptxFile && multipleFiles.json){
        setStatus("submittable")
      } else {
        setStatus("available")
      }
    } else {
      if(file){
        setStatus("submittable")
      }
    }
  }, [file, multipleFiles, props.submissionType])


  const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      props.closeModal();
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      setFile(droppedFile);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsDragging(false);
  };

  // Render PPTX upload UI
  if (props.submissionType === "pptx") {
    return (
      <div
        className="flex justify-center items-center overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none bg-slate-800 bg-opacity-50"
        onClick={handleModalClick}
      >
        <div className="min-w-[600px] max-w-3xl p-6 bg-white rounded shadow-sm relative max-h-[90vh] overflow-y-auto">
          <h3 className="text-xl font-semibold mb-4">ファイルアップロード</h3>
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm text-gray-700">
              <span className="text-red-500 font-semibold">必須:</span> パワーポイントファイル（1〜4のうち少なくとも1つ）、JSONファイル
            </p>
          </div>
          <div className="space-y-4">
            {/* PPTX File 1 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                パワーポイントファイル1 <span className="text-gray-400">（任意）</span>
              </label>
              <input
                type="file"
                accept=".pptx"
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    const file = e.target.files[0];
                    if (!file.name.toLowerCase().endsWith('.pptx')) {
                      toast.error("PPTXファイルのみアップロード可能です", {
                        position: "bottom-left",
                      });
                      e.target.value = '';
                      return;
                    }
                    setMultipleFiles(prev => ({ ...prev, pptx1: file }));
                  }
                }}
              />
              {multipleFiles.pptx1 && (
                <p className="mt-1 text-sm text-slate-600">{multipleFiles.pptx1.name} ({displayFileSize(multipleFiles.pptx1.size)})</p>
              )}
            </div>

            {/* PPTX File 2 (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                パワーポイントファイル2 <span className="text-gray-400">（任意）</span>
              </label>
              <input
                type="file"
                accept=".pptx"
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    const file = e.target.files[0];
                    if (!file.name.toLowerCase().endsWith('.pptx')) {
                      toast.error("PPTXファイルのみアップロード可能です", {
                        position: "bottom-left",
                      });
                      e.target.value = '';
                      return;
                    }
                    setMultipleFiles(prev => ({ ...prev, pptx2: file }));
                  }
                }}
              />
              {multipleFiles.pptx2 && (
                <p className="mt-1 text-sm text-slate-600">{multipleFiles.pptx2.name} ({displayFileSize(multipleFiles.pptx2.size)})</p>
              )}
            </div>

            {/* PPTX File 3 (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                パワーポイントファイル3 <span className="text-gray-400">（任意）</span>
              </label>
              <input
                type="file"
                accept=".pptx"
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    const file = e.target.files[0];
                    if (!file.name.toLowerCase().endsWith('.pptx')) {
                      toast.error("PPTXファイルのみアップロード可能です", {
                        position: "bottom-left",
                      });
                      e.target.value = '';
                      return;
                    }
                    setMultipleFiles(prev => ({ ...prev, pptx3: file }));
                  }
                }}
              />
              {multipleFiles.pptx3 && (
                <p className="mt-1 text-sm text-slate-600">{multipleFiles.pptx3.name} ({displayFileSize(multipleFiles.pptx3.size)})</p>
              )}
            </div>

            {/* PPTX File 4 (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                パワーポイントファイル4 <span className="text-gray-400">（任意）</span>
              </label>
              <input
                type="file"
                accept=".pptx"
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    const file = e.target.files[0];
                    if (!file.name.toLowerCase().endsWith('.pptx')) {
                      toast.error("PPTXファイルのみアップロード可能です", {
                        position: "bottom-left",
                      });
                      e.target.value = '';
                      return;
                    }
                    setMultipleFiles(prev => ({ ...prev, pptx4: file }));
                  }
                }}
              />
              {multipleFiles.pptx4 && (
                <p className="mt-1 text-sm text-slate-600">{multipleFiles.pptx4.name} ({displayFileSize(multipleFiles.pptx4.size)})</p>
              )}
            </div>

            {/* JSON File (Required) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                JSONファイル <span className="text-red-500">*必須</span>
              </label>
              <input
                type="file"
                accept=".json"
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    const file = e.target.files[0];
                    if (!file.name.toLowerCase().endsWith('.json')) {
                      toast.error("JSONファイルのみアップロード可能です", {
                        position: "bottom-left",
                      });
                      e.target.value = '';
                      return;
                    }
                    setMultipleFiles(prev => ({ ...prev, json: file }));
                  }
                }}
              />
              {multipleFiles.json && (
                <p className="mt-1 text-sm text-slate-600">{multipleFiles.json.name} ({displayFileSize(multipleFiles.json.size)})</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-center gap-8 mt-8">
            <button onClick={handleFileSubmit} disabled={status !== "submittable"}>
              {status === "uploading" && <span>送信中...</span>}
              {status !== "uploading" && "アップロード"}
            </button>
            <button className="text-maincolor bg-white border border-maincolor" onClick={() => props.closeModal()}>キャンセル</button>
          </div>

          {status === "uploading" && (
            <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-75 z-10">
              <ClipLoader color="#4A90E2" size={50} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render standard single file upload UI
  return (
    <div
      className="flex justify-center items-center overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none bg-slate-800 bg-opacity-50"
      onClick={handleModalClick}
    >
      <div className="min-w-96 p-6 bg-white rounded shadow-sm relative">
        <div className="flex items-center justify-center flex-col w-full">
          <label
            htmlFor="file"
            className={`flex flex-col items-center justify-center w-full h-56 border-2 border-dashed rounded-lg cursor-pointer  ${
            isDragging ? "bg-blue-100 border-blue-500" : "bg-gray-50 border-gray-300"
          }`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragLeave={handleDragLeave}
          >
            <div className="flex flex-col items-center justify-center px-8 py-12">
              <AiOutlineCloudUpload size={76} className="mb-4" color="#279AA680" />
              <p className="mb-2 text-lg text-gray-500 dark:text-gray-400">
                <span className="font-semibold">ファイルをここにドラッグ＆ドロップ</span>
              </p>
              <p className="text-gray-500">
                {fileInputAccept}
              </p>
            </div>
            <input
              id="file"
              type="file"
              className="hidden"
              multiple={false}
              accept={fileInputAccept}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]){
                  setFile(e.target.files[0])
                }}}
            />
          </label>
          {
            file && (
              <p className="mt-4 text-sm text-slate-600">{file.name} {displayFileSize(file.size)}</p>
            )
          }
        </div>
        <div className="flex items-center justify-center gap-8 mt-8">
        <button onClick={handleFileSubmit} disabled={status !== "submittable"}>{status === "uploading" && <span>送信中</span>}アップロード</button>
        <button className="text-maincolor bg-white border border-maincolor" onClick={() => props.closeModal()}>キャンセル</button>
        </div>
      </div>
      {status === "uploading" && (
          <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-75 z-10">
            <ClipLoader color="#4A90E2" size={50} />
          </div>
      )}
    </div>
  );
}

export default FileUpload;
